import { select } from 'd3';
import { useCallback, useLayoutEffect, useRef } from 'react';

type UseD3SvgSelectionOptions<TItem> = {
  /** Ref to the layer <g> that contains selectable children */
  layerRef: React.RefObject<SVGGElement | null>;
  /** Attribute used to identify items (e.g. 'data-hole-name') */
  dataAttribute: `data-${string}`;
  /** Controlled selected id */
  selectedId: string | null;
  /** Lookup item by its id */
  getItemById: (id: string) => TItem | null;
  /** Called when an item is activated (click / Enter / Space) */
  onSelect?: (item: TItem) => void;
  /** Apply/remove selected styles to a single group node */
  applySelected?: (group: SVGGElement, isSelected: boolean) => void;
};

/** Escapes a string for use in a CSS attribute selector */
const escapeForCssAttr = (value: string) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
};

/**
 * Generic hook for delegated SVG selection and hover with D3.
 *
 * Attaches delegated click, keydown, pointerenter and pointerleave listeners
 * to a parent `<g>` layer, identifies the target via a `data-*` attribute,
 * and applies selection/hover styles imperatively (no per-item React re-renders).
 * This approach is necessary for performance with large numbers of items (like drill holes)
 * and to allow D3 to manage attributes like fill and stroke without React overwriting them.
 */
const useD3SvgSelection = <TItem>({
  layerRef,
  dataAttribute,
  selectedId,
  getItemById,
  onSelect,
  applySelected,
}: UseD3SvgSelectionOptions<TItem>) => {
  const prevSelectedIdRef = useRef<string | null>(null);

  // Store latest values in refs so D3 listeners and effects never go stale
  const getItemByIdRef = useRef(getItemById);
  getItemByIdRef.current = getItemById;

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const applySelectedRef = useRef(applySelected);
  applySelectedRef.current = applySelected;

  const dataAttributeRef = useRef(dataAttribute);
  dataAttributeRef.current = dataAttribute;

  /** Finds a child `<g>` by its data attribute value. Stable — reads refs internally. */
  const findGroupById = useCallback(
    (id: string): SVGGElement | null => {
      const layer = layerRef.current;
      if (!layer) return null;

      const safe = escapeForCssAttr(id);
      return layer.querySelector<SVGGElement>(`[${dataAttributeRef.current}="${safe}"]`);
    },
    [layerRef],
  );

  /** Deselects the previously selected `<g>` if it differs from the new id. */
  const deselectPrevious = useCallback(
    (newId: string) => {
      const prev = prevSelectedIdRef.current;
      if (!prev || prev === newId) return;

      const prevGroup = findGroupById(prev);
      if (prevGroup && applySelectedRef.current) applySelectedRef.current(prevGroup, false);
    },
    [findGroupById],
  );

  /** Handles selection from a delegated DOM event. */
  const activateFromEvent = useCallback(
    (event: Event) => {
      const attr = dataAttributeRef.current;
      const target = (event.target as Element | null)?.closest<SVGGElement>(`[${attr}]`);
      const id = target?.getAttribute(attr) ?? null;
      if (!id || !target) return;

      const item = getItemByIdRef.current(id);
      if (!item) return; // Don't select if item doesn't exist

      deselectPrevious(id);

      if (applySelectedRef.current) applySelectedRef.current(target, true);
      prevSelectedIdRef.current = id;

      if (onSelectRef.current) onSelectRef.current(item);
    },
    [deselectPrevious],
  );

  // Attach delegated listeners ONCE
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    const d3Layer = select(layer);

    d3Layer.on('click.selection', (event: Event) => activateFromEvent(event));

    d3Layer.on('keydown.selection', (event: Event) => {
      const e = event as KeyboardEvent;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      activateFromEvent(e);
    });

    return () => {
      d3Layer.on('click.selection', null);
      d3Layer.on('keydown.selection', null);
    };
  }, [layerRef, activateFromEvent]);

  // Sync controlled selection (for external state changes like clearing)
  useLayoutEffect(() => {
    const prev = prevSelectedIdRef.current;
    const next = selectedId;

    if (prev === next) return;

    if (prev) {
      const prevGroup = findGroupById(prev);
      if (prevGroup && applySelectedRef.current) applySelectedRef.current(prevGroup, false);
    }

    if (next) {
      const nextGroup = findGroupById(next);
      if (nextGroup && applySelectedRef.current) applySelectedRef.current(nextGroup, true);
    }

    prevSelectedIdRef.current = next;
  }, [selectedId, findGroupById]);
};

export { type UseD3SvgSelectionOptions, useD3SvgSelection };
